import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Switch from '@material-ui/core/Switch';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import CropFreeIcon from '@material-ui/icons/CropFree';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import coinSpendCodesClient from '../../../../../../clients/coin-spend-codes';
import useDialog from '../../../../../../web-app-components/hooks/useDialog';

import SpendQRCodeDialog from '../SpendQRCodeDialog';

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  list: {
    maxHeight: 'calc(100vh - 420px)',
    overflowY: 'auto',
  },
  item: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  itemName: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  itemMeta: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  itemActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  itemActionsRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  itemDisabled: {
    opacity: 0.7,
    backgroundColor: theme.palette.action.hover,
  },
  disabledBadge: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    marginLeft: theme.spacing(1),
  },
  addSection: {
    padding: theme.spacing(2),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  addField: {
    marginBottom: theme.spacing(2),
  },
  emptyState: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}));

const ObjectSpendCodesSidebar = ({ objectId }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogCode, setDialogCode] = useState(null);
  const deleteConfirmDialog = useDialog();
  const [codeToDelete, setCodeToDelete] = useState(null);

  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCoins, setNewCoins] = useState('');
  const [creating, setCreating] = useState(false);

  const [codeToEdit, setCodeToEdit] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCoins, setEditCoins] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchCodes = async () => {
    if (!objectId) return;
    try {
      setLoading(true);
      const list = await coinSpendCodesClient.findByObject(objectId);
      setCodes(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching spend codes:', error);
      enqueueSnackbar('Nepavyko gauti QR kodų sąrašo', { variant: 'error' });
      setCodes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCodes();
  }, [objectId]);

  const handleShowQR = (code) => {
    setDialogCode(code);
  };

  const handleCloseDialog = () => {
    setDialogCode(null);
  };

  const handleDeleteClick = (code) => {
    setCodeToDelete(code);
    deleteConfirmDialog.setOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!codeToDelete) return;
    const id = codeToDelete.documentId ?? codeToDelete.id;
    deleteConfirmDialog.setOpen(false);
    setCodeToDelete(null);
    if (!id) return;
    try {
      await coinSpendCodesClient.remove(id);
      enqueueSnackbar('QR kodas ištrintas', { variant: 'success' });
      await fetchCodes();
      if (dialogCode && (dialogCode.documentId === id || dialogCode.id === id)) setDialogCode(null);
    } catch (error) {
      console.error('Error deleting spend code:', error);
      enqueueSnackbar('Nepavyko ištrinti QR kodo', { variant: 'error' });
    }
  };

  const handleToggleEnabled = async (code) => {
    const id = code.documentId ?? code.id;
    if (!id) return;
    const nextEnabled = !(code.Enabled !== false);
    try {
      await coinSpendCodesClient.update(id, { Enabled: nextEnabled });
      enqueueSnackbar(nextEnabled ? 'QR kodas įjungtas' : 'QR kodas išjungtas', { variant: 'success' });
      await fetchCodes();
    } catch (error) {
      console.error('Error toggling spend code:', error);
      enqueueSnackbar('Nepavyko pakeisti būsenos', { variant: 'error' });
    }
  };

  const handleEditClick = (code) => {
    setCodeToEdit(code);
    setEditName(code.Name ?? '');
    setEditDescription(code.Description ?? '');
    setEditCoins(String(code.Coins ?? ''));
  };

  const handleEditClose = () => {
    setCodeToEdit(null);
    setEditName('');
    setEditDescription('');
    setEditCoins('');
  };

  const handleEditSave = async () => {
    if (!codeToEdit) return;
    const name = (editName || '').trim();
    const coins = parseInt(editCoins, 10);
    if (!name || !Number.isFinite(coins) || coins < 1) {
      enqueueSnackbar('Įveskite pavadinimą ir teigiamą auksinukų skaičių', { variant: 'warning' });
      return;
    }
    const id = codeToEdit.documentId ?? codeToEdit.id;
    if (!id) return;
    try {
      setSaving(true);
      await coinSpendCodesClient.update(id, {
        Name: name,
        Coins: coins,
        ...((editDescription || '').trim() ? { Description: (editDescription || '').trim() } : { Description: '' }),
      });
      enqueueSnackbar('QR kodas atnaujintas', { variant: 'success' });
      handleEditClose();
      await fetchCodes();
      if (dialogCode && (dialogCode.documentId === id || dialogCode.id === id)) {
        setDialogCode((prev) => (prev ? { ...prev, Name: name, Description: (editDescription || '').trim(), Coins: coins } : null));
      }
    } catch (error) {
      console.error('Error updating spend code:', error);
      enqueueSnackbar('Nepavyko atnaujinti QR kodo', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    const name = (newName || '').trim();
    const coins = parseInt(newCoins, 10);
    if (!name || !Number.isFinite(coins) || coins < 1) {
      enqueueSnackbar('Įveskite pavadinimą ir teigiamą auksinukų skaičių', { variant: 'warning' });
      return;
    }
    try {
      setCreating(true);
      await coinSpendCodesClient.create({
        Name: name,
        Coins: coins,
        Object: objectId,
        ...((newDescription || '').trim() ? { Description: (newDescription || '').trim() } : {}),
      });
      enqueueSnackbar('QR kodas sukurtas', { variant: 'success' });
      setNewName('');
      setNewDescription('');
      setNewCoins('');
      await fetchCodes();
    } catch (error) {
      console.error('Error creating spend code:', error);
      enqueueSnackbar('Nepavyko sukurti QR kodo', { variant: 'error' });
    } finally {
      setCreating(false);
    }
  };

  if (!objectId) return null;

  return (
    <div className={classes.root}>
      <Typography className={classes.title} variant="h6">
        QR kodai (monetų išleidimas)
      </Typography>

      <div className={classes.addSection}>
        <Typography variant="subtitle2" gutterBottom>
          Generuoti naują QR kodą
        </Typography>
        <TextField
          className={classes.addField}
          label="Pavadinimas"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          fullWidth
          size="small"
        />
        <TextField
          className={classes.addField}
          label="Aprašymas"
          value={newDescription}
          onChange={(e) => setNewDescription(e.target.value)}
          fullWidth
          size="small"
        />
        <TextField
          className={classes.addField}
          label="Auksinukų skaičius"
          type="number"
          inputProps={{ min: 1 }}
          value={newCoins}
          onChange={(e) => setNewCoins(e.target.value)}
          fullWidth
          size="small"
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreate}
          disabled={creating}
          fullWidth
        >
          {creating ? 'Kuriama...' : 'Generuoti QR kodą'}
        </Button>
      </div>

      {loading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <div className={classes.list}>
          {codes.length === 0 ? (
            <div className={classes.emptyState}>
              Nėra sugeneruotų QR kodų.
            </div>
          ) : (
            codes.map((code) => {
              const enabled = code.Enabled !== false;
              return (
                <Paper
                  key={code.id || code.documentId}
                  className={enabled ? classes.item : `${classes.item} ${classes.itemDisabled}`}
                  elevation={0}
                >
                  <Typography className={classes.itemName} variant="body1">
                    {code.Name}
                    {!enabled && (
                      <span className={classes.disabledBadge}> (išjungtas)</span>
                    )}
                  </Typography>
                  <Typography className={classes.itemMeta} variant="body2">
                    {code.Coins} auksinukų
                    {code.Description ? ` · ${code.Description}` : ''}
                  </Typography>
                  <div className={classes.itemActions}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<CropFreeIcon />}
                      onClick={() => handleShowQR(code)}
                    >
                      Rodyti QR
                    </Button>
                    <div className={classes.itemActionsRight}>
                      <Tooltip title={enabled ? 'Išjungti QR kodą' : 'Įjungti QR kodą'}>
                        <Switch
                          checked={enabled}
                          onChange={() => handleToggleEnabled(code)}
                          color="primary"
                          size="medium"
                          inputProps={{
                            'aria-label': enabled ? 'Išjungti QR kodą' : 'Įjungti QR kodą',
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Redaguoti QR kodą">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(code)}
                          aria-label="Redaguoti"
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Ištrinti QR kodą">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(code)}
                          aria-label="Ištrinti"
                          color="secondary"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </Paper>
              );
            })
          )}
        </div>
      )}

      {dialogCode && (
        <SpendQRCodeDialog
          open={!!dialogCode}
          onClose={handleCloseDialog}
          objectDocumentId={objectId}
          codeDocumentId={dialogCode.id ?? dialogCode.documentId}
          name={dialogCode.Name}
        />
      )}

      <Dialog open={!!codeToEdit} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Redaguoti QR kodą</DialogTitle>
        <DialogContent>
          <TextField
            className={classes.addField}
            label="Pavadinimas"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            fullWidth
            size="small"
            margin="normal"
          />
          <TextField
            className={classes.addField}
            label="Aprašymas"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            fullWidth
            size="small"
            margin="normal"
          />
          <TextField
            className={classes.addField}
            label="Auksinukų skaičius"
            type="number"
            inputProps={{ min: 1 }}
            value={editCoins}
            onChange={(e) => setEditCoins(e.target.value)}
            fullWidth
            size="small"
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Atšaukti</Button>
          <Button onClick={handleEditSave} color="primary" variant="contained" disabled={saving}>
            {saving ? 'Išsaugoma...' : 'Išsaugoti'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmDialog.open} onClose={deleteConfirmDialog.onClose}>
        <DialogContent>
          <DialogContentText>
            {codeToDelete
              ? `Ar tikrai norite ištrinti QR kodą „${codeToDelete.Name}"? Atkurti bus neįmanoma.`
              : 'Ar tikrai norite ištrinti šį QR kodą?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirm} color="primary" variant="contained">
            Taip
          </Button>
          <Button onClick={deleteConfirmDialog.onClose}>Atšaukti</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

ObjectSpendCodesSidebar.propTypes = {
  objectId: PropTypes.string.isRequired,
};

export default ObjectSpendCodesSidebar;
